import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, LockSimple, TrendUp, UsersThree, Lightning, ShieldCheck, CaretLeft, CaretRight, Target, ChartLineUp, Buildings, ArrowLeft } from '@phosphor-icons/react';

const ROLE_COPY = {
  startup: {
    eyebrow: 'FOR FOUNDERS',
    slides: [
      {
        headline: 'Build once. Be investor-ready everywhere.',
        body: 'Turn your startup profile into a reusable fundraising workspace for applications, updates and investor conversations.',
        bullets: [
          ['One founder profile', 'Keep your company story, traction and key information in one place.'],
          ['Faster applications', 'Reuse structured startup information instead of starting from scratch.'],
          ['Stay discoverable', 'Give relevant investors and programs the context they need to understand you.'],
        ],
        kicker: 'Your FounderBoard profile becomes the foundation for everything you do on the platform.',
      },
      {
        headline: 'Know what your numbers are saying.',
        body: 'FounderBoard helps you organise operating updates and compare progress with the context that matters for your stage.',
        bullets: [
          ['Monthly updates', 'Build a consistent record of progress, metrics and milestones.'],
          ['Useful benchmarks', 'Put performance into context instead of looking at numbers in isolation.'],
          ['Clearer storytelling', 'Connect traction, goals and company progress for future conversations.'],
        ],
        kicker: 'Less time assembling updates. More time understanding what changed and what comes next.',
      },
      {
        headline: 'Find the right path to your next milestone.',
        body: 'Use FounderBoard to discover relevant programs, build investor context and keep your fundraising journey organised.',
        bullets: [
          ['Program discovery', 'Find opportunities that match your startup profile and stage.'],
          ['Investor context', 'Present a structured picture of the business when conversations begin.'],
          ['A growing workspace', 'Keep your profile, vault, updates and fundraising activity connected.'],
        ],
        kicker: 'Set up your profile now, then keep improving it as your company grows.',
      },
    ],
  },
  vc: {
    eyebrow: 'FOR INVESTORS',
    slides: [
      {
        headline: 'Turn your investment thesis into better deal flow.',
        body: 'Set your sectors, stages and cheque preferences so FounderBoard can organise discovery around the way you actually invest.',
        bullets: [
          ['Thesis-led discovery', 'Focus startup discovery around the criteria that matter to your fund.'],
          ['Structured profiles', 'Review consistent company information before the first conversation.'],
          ['Less manual triage', 'Spend less time piecing together basic startup context.'],
        ],
        kicker: 'Your onboarding choices shape the investment context FounderBoard uses for your workspace.',
      },
      {
        headline: 'Keep portfolio context in one operating view.',
        body: 'Bring company updates, portfolio information and recurring founder reporting closer together.',
        bullets: [
          ['Portfolio visibility', 'Follow company progress without hunting across disconnected updates.'],
          ['Monthly reporting', 'Keep recurring startup data organised over time.'],
          ['Shared context', 'Give your team a clearer picture of portfolio activity.'],
        ],
        kicker: 'A cleaner operating layer for the information that sits between meetings.',
      },
      {
        headline: 'Move from sourcing to conviction with more context.',
        body: 'FounderBoard is designed to help investors evaluate, track and revisit startups without losing the story behind the data.',
        bullets: [
          ['Consistent evaluation', 'Start from structured startup information across your pipeline.'],
          ['Track what matters', 'Keep relevant companies and their progress within reach.'],
          ['Founder relationships', 'Carry context forward as companies evolve.'],
        ],
        kicker: 'Set up your fund profile once and make the rest of the platform more relevant from day one.',
      },
    ],
  },
  incubator: {
    eyebrow: 'FOR INCUBATORS',
    slides: [
      {
        headline: 'Build a stronger founder pipeline.',
        body: 'Define your program focus so FounderBoard can help organise startup discovery and applications around the founders you want to support.',
        bullets: [
          ['Relevant applicants', 'Align startup discovery with your sectors, stages and program focus.'],
          ['Structured applications', 'Collect consistent founder and company information for review.'],
          ['Faster screening', 'Reduce the manual work required to understand every application.'],
        ],
        kicker: 'Your incubator profile gives FounderBoard the context needed to make program workflows more relevant.',
      },
      {
        headline: 'Run programs with less operational clutter.',
        body: 'Keep applications, startup information and cohort context closer together throughout the program lifecycle.',
        bullets: [
          ['Application management', 'Review startup submissions with the information your team needs.'],
          ['Cohort visibility', 'Keep participating company context organised in one workspace.'],
          ['Repeatable workflows', 'Use a more consistent process across programs and cohorts.'],
        ],
        kicker: 'Spend less time organising information and more time supporting the companies in your program.',
      },
      {
        headline: 'Stay connected to founder progress after selection.',
        body: 'FounderBoard helps you keep a clearer view of startup progress, updates and program relationships beyond the application stage.',
        bullets: [
          ['Founder progress', 'Keep important company context accessible as startups move forward.'],
          ['Program continuity', 'Carry information from application through ongoing engagement.'],
          ['Better context', 'Give your team a more complete picture when supporting founders.'],
        ],
        kicker: 'Create your incubator profile once, then build your programs and founder network on top of it.',
      },
    ],
  },
};

const STEP_META = {
  startup: [
    ['Your Startup', 'A few essentials to personalise FounderBoard'],
  ],
  vc: [
    ['Your Fund', 'Organisation, role, and portfolio context'],
    ['Investment Thesis', 'Sectors, stages, and cheque preferences'],
  ],
  incubator: [
    ['Your Incubator', 'Organisation, role, and cohort context'],
    ['Program Focus', 'Sectors, stages, and program preferences'],
  ],
};

const SignupOnboardingShell = ({ role = 'startup', step = 1, children, singleSection = false, programContinuation = false }) => {
  const copy = ROLE_COPY[role] || ROLE_COPY.startup;
  const [marketingSlide, setMarketingSlide] = useState(0);
  const [slideDirection, setSlideDirection] = useState(1);
  const slides = copy.slides;
  const activeSlide = slides[marketingSlide];
  const steps = STEP_META[role] || STEP_META.startup;

  useEffect(() => {
    setMarketingSlide(0);
    setSlideDirection(1);
  }, [role]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSlideDirection(1);
      setMarketingSlide((currentSlide) => (currentSlide + 1) % slides.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [slides.length]);
  const totalSteps = steps.length;
  const completion = Math.round((step / totalSteps) * 100);
  const current = steps[Math.min(step - 1, totalSteps - 1)];
  const hideProgress = role === 'startup' || singleSection;

  return (
    <div className="fb-signup-shell min-h-screen w-full overflow-hidden bg-[#f6f7f9] text-[#111827]">
      <style>{`
        @media (min-width: 1024px) {
          .fb-signup-shell {
            display: grid;
            grid-template-columns: 36% 64%;
            animation: fbExpandSignupPanel 760ms cubic-bezier(.22,.78,.22,1) both;
          }
          @keyframes fbExpandSignupPanel {
            from { grid-template-columns: 60% 40%; }
            to { grid-template-columns: 36% 64%; }
          }
          .fb-signup-form-enter {
            animation: fbSignupFormEnter 620ms 120ms cubic-bezier(.22,.78,.22,1) both;
          }
          @keyframes fbSignupFormEnter {
            from { opacity: .15; transform: translateX(24px); }
            to { opacity: 1; transform: translateX(0); }
          }
        }
        .signup-form-content > div > h2,
        .signup-form-content > div > p { display: none; }
        .signup-form-content > div > div:first-of-type {
          background: transparent;
          border: 0;
          border-radius: 0;
          padding: 0;
        }
        .signup-form-content label {
          display: inline-block;
          margin-bottom: .3rem;
          color: #475569;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: .075em;
          text-transform: uppercase;
        }
        .signup-form-content input:not([type='checkbox']):not([type='radio']),
        .signup-form-content select,
        .signup-form-content textarea {
          width: 100%;
          min-height: 44px;
          border: 1px solid #dbe2ea;
          border-radius: 10px;
          background-color: #fbfcfe;
          color: #0f172a;
          font-size: 16px;
          line-height: 1.4;
          box-shadow: 0 1px 2px rgba(15,23,42,.02), inset 0 1px 0 rgba(255,255,255,.9);
          transition: border-color 180ms ease, box-shadow 180ms ease, background-color 180ms ease, transform 180ms ease;
        }
        @media (min-width: 768px) {
          .signup-form-content input:not([type='checkbox']):not([type='radio']),
          .signup-form-content select,
          .signup-form-content textarea {
            font-size: 12px;
          }
        }
        .signup-form-content input:not([type='checkbox']):not([type='radio']) { padding: .62rem .8rem; }
        .signup-form-content textarea { min-height: 92px; padding: .62rem .8rem; resize: vertical; }
        .signup-form-content select {
          appearance: none;
          -webkit-appearance: none;
          padding: .62rem 2.5rem .62rem .8rem;
          cursor: pointer;
          background-image:
            linear-gradient(45deg, transparent 50%, #64748b 50%),
            linear-gradient(135deg, #64748b 50%, transparent 50%);
          background-position:
            calc(100% - 17px) calc(50% - 2px),
            calc(100% - 12px) calc(50% - 2px);
          background-size: 5px 5px, 5px 5px;
          background-repeat: no-repeat;
        }
        .signup-form-content input:not([type='checkbox']):not([type='radio']):hover,
        .signup-form-content select:hover,
        .signup-form-content textarea:hover {
          border-color: #c4b5fd;
          background-color: #fff;
        }
        .signup-form-content input:not([type='checkbox']):not([type='radio']):focus,
        .signup-form-content select:focus,
        .signup-form-content textarea:focus {
          outline: none;
          border-color: #8b5cf6;
          background-color: #fff;
          box-shadow: 0 0 0 3px rgba(139,92,246,.11), 0 4px 14px rgba(15,23,42,.035);
        }
        .signup-form-content input::placeholder,
        .signup-form-content textarea::placeholder { color: #a8b1bf; }
        .signup-form-content select:disabled,
        .signup-form-content input:disabled,
        .signup-form-content textarea:disabled {
          cursor: not-allowed;
          background-color: #f1f5f9;
          color: #94a3b8;
          opacity: .8;
        }
        .signup-form-content input[type='checkbox'],
        .signup-form-content input[type='radio'] {
          accent-color: #7c3aed;
        }
        .signup-form-content > div > div:first-of-type > :not([hidden]) ~ :not([hidden]) { margin-top: .78rem; }
        /* Keep question groups compact without making the form feel dense. */
        .signup-form-content .space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: .78rem !important; }
        .signup-form-content .space-y-5 > :not([hidden]) ~ :not([hidden]) { margin-top: .9rem !important; }
        .signup-form-content .space-y-6 > :not([hidden]) ~ :not([hidden]) { margin-top: 1rem !important; }
        .signup-form-content p { line-height: 1.45; }
        .signup-form-content button { transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease, color 180ms ease; }
        .signup-form-content button:not(:disabled):active { transform: translateY(1px); }
        .fb-step-card {
          animation: fbStepCardIn 520ms cubic-bezier(.16,1,.3,1) both;
          transform-origin: 50% 22%;
        }
        .fb-step-header { animation: fbStepHeaderIn 420ms 55ms cubic-bezier(.16,1,.3,1) both; }
        .fb-step-form { animation: fbStepFormIn 500ms 100ms cubic-bezier(.16,1,.3,1) both; }
        .fb-step-list { animation: fbStepListIn 500ms 150ms cubic-bezier(.16,1,.3,1) both; }
        .fb-progress-fill { position: relative; overflow: hidden; }
        .fb-progress-fill::after {
          content: '';
          position: absolute;
          inset: 0;
          transform: translateX(-100%);
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.55), transparent);
          animation: fbProgressSweep 850ms 160ms ease-out both;
        }
        @keyframes fbStepCardIn {
          from { opacity: 0; transform: translate3d(26px, 10px, 0) scale(.985); filter: blur(2px); }
          to { opacity: 1; transform: translate3d(0,0,0) scale(1); filter: blur(0); }
        }
        @keyframes fbStepHeaderIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fbStepFormIn {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fbStepListIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fbProgressSweep {
          from { transform: translateX(-100%); }
          to { transform: translateX(110%); }
        }
        .signup-form-content [data-testid="step1-continue-button"],
        .signup-form-content [data-testid="step2-continue-button"],
        .signup-form-content [data-testid="step3-continue-button"] {
          width: auto;
          min-width: 158px;
          margin-left: auto;
          padding-left: 1.15rem;
          padding-right: 1.15rem;
          background: #11111b;
          border-color: #11111b;
          color: #fff;
          font-size: 11px;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(15, 23, 42, .09);
        }
        .signup-form-content [data-testid="step1-continue-button"]:hover,
        .signup-form-content [data-testid="step2-continue-button"]:hover,
        .signup-form-content [data-testid="step3-continue-button"]:hover { background: #242433; }
        .signup-form-content [data-testid="step1-continue-button"]:disabled,
        .signup-form-content [data-testid="step2-continue-button"]:disabled,
        .signup-form-content [data-testid="step3-continue-button"]:disabled { opacity: .45; }
        .fb-marketing-stage {
          position: relative;
          isolation: isolate;
        }
        .fb-marketing-stage::before {
          content: '';
          position: absolute;
          z-index: -1;
          width: 230px;
          height: 230px;
          left: -74px;
          top: 12%;
          border-radius: 9999px;
          background: radial-gradient(circle, rgba(139,92,246,.13) 0%, rgba(52,211,153,.05) 38%, transparent 72%);
          filter: blur(8px);
          animation: fbMarketingGlow 900ms cubic-bezier(.22,.78,.22,1) both;
        }
        .fb-marketing-slide {
          transform-origin: 50% 45%;
          will-change: transform, opacity, filter;
        }
        .fb-marketing-slide[data-direction='next'] {
          animation: fbMarketingSlideNext 720ms cubic-bezier(.16,1,.3,1) both;
        }
        .fb-marketing-slide[data-direction='prev'] {
          animation: fbMarketingSlidePrev 720ms cubic-bezier(.16,1,.3,1) both;
        }
        .fb-marketing-slide h1 {
          animation: fbMarketingHeadline 760ms 70ms cubic-bezier(.16,1,.3,1) both;
        }
        .fb-marketing-slide > p {
          animation: fbMarketingBody 680ms 150ms cubic-bezier(.16,1,.3,1) both;
        }
        .fb-marketing-bullet {
          opacity: 0;
          animation: fbMarketingBullet 620ms cubic-bezier(.16,1,.3,1) both;
        }
        .fb-marketing-kicker {
          opacity: 0;
          animation: fbMarketingKicker 700ms 430ms cubic-bezier(.16,1,.3,1) both;
        }
        @keyframes fbMarketingSlideNext {
          0% { opacity: 0; transform: translate3d(58px, 8px, 0) scale(.965); filter: blur(5px); }
          55% { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
        }
        @keyframes fbMarketingSlidePrev {
          0% { opacity: 0; transform: translate3d(-58px, 8px, 0) scale(.965); filter: blur(5px); }
          55% { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: translate3d(0, 0, 0) scale(1); filter: blur(0); }
        }
        @keyframes fbMarketingHeadline {
          from { opacity: 0; transform: translateY(18px) scale(.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fbMarketingBody {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fbMarketingBullet {
          from { opacity: 0; transform: translateX(24px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fbMarketingKicker {
          from { opacity: 0; transform: translateY(15px) scale(.985); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fbMarketingGlow {
          from { opacity: 0; transform: translateX(40px) scale(.75); }
          55% { opacity: 1; }
          to { opacity: .85; transform: translateX(0) scale(1); }
        }
        @media (min-width: 1024px) {
          .fb-signup-marketing-panel {
            height: 100vh;
            min-height: 0;
          }
          .fb-signup-marketing-content {
            min-height: 0;
          }
        }
        @media (min-width: 1024px) and (max-height: 820px) {
          .fb-signup-marketing-panel { padding-top: 1.35rem !important; padding-bottom: 1.35rem !important; }
          .fb-signup-marketing-content { padding-top: 1.25rem !important; padding-bottom: 1.25rem !important; }
          .fb-signup-marketing-content > div:first-child { margin-bottom: .85rem !important; }
          .fb-signup-marketing-content h1 { font-size: 30px !important; line-height: 1.05 !important; }
          .fb-signup-marketing-content .fb-marketing-slide > p { margin-top: .75rem !important; line-height: 1.55 !important; }
          .fb-signup-marketing-content .fb-marketing-slide > div:nth-of-type(1) { margin-top: 1rem !important; }
          .fb-signup-marketing-content .fb-marketing-bullet { padding-top: .55rem !important; padding-bottom: .55rem !important; }
          .fb-signup-marketing-content .fb-marketing-kicker { margin-top: 1rem !important; padding: .75rem !important; }
          .fb-signup-marketing-content > div:last-child { margin-top: 1rem !important; }
        }
        @media (min-width: 1024px) and (max-height: 700px) {
          .fb-signup-marketing-content h1 { font-size: 27px !important; }
          .fb-signup-marketing-content .fb-marketing-slide > p { font-size: 11px !important; }
          .fb-signup-marketing-bullet-description { display: none; }
          .fb-signup-marketing-content .fb-marketing-kicker { display: none; }
        }
        @media (max-width: 1023px) {
          .fb-signup-shell { overflow: visible; }
          .fb-signup-mobile-intro {
            background: #0d0c16;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .fb-signup-shell, .fb-signup-form-enter, .fb-marketing-slide,
          .fb-marketing-slide h1, .fb-marketing-slide > p, .fb-marketing-bullet,
          .fb-marketing-kicker, .fb-marketing-stage::before, .fb-step-card, .fb-step-header,
          .fb-step-form, .fb-step-list, .fb-progress-fill::after { animation: none !important; }
          .fb-marketing-bullet, .fb-marketing-kicker { opacity: 1 !important; }
        }
      `}</style>

      <aside className="fb-signup-marketing-panel relative hidden h-screen overflow-hidden bg-[#0d0c16] py-8 text-white lg:flex lg:flex-col lg:px-12 xl:px-16 xl:py-9 2xl:px-20">
        <div className="pointer-events-none absolute -left-32 top-20 h-80 w-80 rounded-full border border-violet-400/10" />
        <div className="pointer-events-none absolute -left-20 top-36 h-56 w-56 rounded-full bg-emerald-400/[0.035] blur-2xl" />
        <div className="pointer-events-none absolute inset-x-0 bottom-[25%] h-px bg-white/[0.05]" />

        <Link to="/" className="relative z-10 inline-flex w-fit items-center gap-3" aria-label="FounderBoard home">
          <img src="/founderboard-logo.png" alt="FounderBoard" className="h-9 w-9 rounded-lg object-contain" />
          <span className="font-heading text-[15px] font-bold tracking-[-0.2px]">FounderBoard</span>
        </Link>

        <div className="fb-signup-marketing-content relative z-10 my-auto w-full max-w-[430px] py-8 xl:max-w-[470px] xl:py-10">
          <div className="mb-6 flex items-center justify-between gap-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-violet-300">{copy.eyebrow}</p>
            <span className="text-[10px] font-semibold tabular-nums text-slate-600">0{marketingSlide + 1} / 0{slides.length}</span>
          </div>

          <div className="fb-marketing-stage">
            <div
              key={`${role}-${marketingSlide}`}
              className="fb-marketing-slide"
              data-direction={slideDirection > 0 ? 'next' : 'prev'}
            >
            <h1 className="font-heading text-[34px] font-bold leading-[1.08] tracking-[-0.035em] xl:text-[40px] 2xl:text-[44px]">{activeSlide.headline}</h1>
            <p className="mt-5 max-w-[410px] text-[12px] leading-[1.75] text-slate-400">{activeSlide.body}</p>

            <div className="mt-8 space-y-1">
              {activeSlide.bullets.map(([title, description], index) => {
                const icons = [Target, ChartLineUp, Buildings];
                const Icon = icons[index] || CheckCircle;
                return (
                  <div
                    key={title}
                    className="fb-marketing-bullet flex gap-3.5 border-b border-white/[0.06] py-3.5 last:border-0"
                    style={{ animationDelay: `${220 + index * 95}ms` }}
                  >
                    <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-400/10 text-emerald-300">
                      <Icon size={15} weight="bold" />
                    </div>
                    <div>
                      <div className="text-[12px] font-semibold text-slate-100">{title}</div>
                      <div className="fb-signup-marketing-bullet-description mt-1 max-w-[360px] text-[10px] leading-[1.6] text-slate-500">{description}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="fb-marketing-kicker mt-8 rounded-xl border border-violet-300/10 bg-white/[0.045] p-4 shadow-[0_14px_38px_rgba(0,0,0,0.12)]">
              <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-[0.15em] text-violet-300">
                <Lightning size={12} weight="fill" /> Why FounderBoard
              </div>
              <p className="mt-2 text-[10px] leading-[1.65] text-slate-400">{activeSlide.kicker}</p>
            </div>
            </div>
          </div>

          <div className="mt-7 flex items-center justify-between">
            <div className="flex items-center gap-2" aria-label="Marketing carousel pages">
              {slides.map((slide, index) => (
                <button
                  key={slide.headline}
                  type="button"
                  aria-label={`Show marketing page ${index + 1}`}
                  aria-current={marketingSlide === index ? 'true' : undefined}
                  onClick={() => {
                    if (index === marketingSlide) return;
                    setSlideDirection(index > marketingSlide ? 1 : -1);
                    setMarketingSlide(index);
                  }}
                  className={`h-1.5 rounded-full transition-all duration-300 ${marketingSlide === index ? 'w-7 bg-violet-400' : 'w-1.5 bg-white/20 hover:bg-white/35'}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label="Previous marketing page"
                onClick={() => {
                  setSlideDirection(-1);
                  setMarketingSlide((marketingSlide - 1 + slides.length) % slides.length);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/[0.035] text-slate-400 transition hover:border-white/20 hover:bg-white/[0.07] hover:text-white"
              >
                <CaretLeft size={14} weight="bold" />
              </button>
              <button
                type="button"
                aria-label="Next marketing page"
                onClick={() => {
                  setSlideDirection(1);
                  setMarketingSlide((marketingSlide + 1) % slides.length);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-white/[0.035] text-slate-400 transition hover:border-white/20 hover:bg-white/[0.07] hover:text-white"
              >
                <CaretRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </div>

        <div className="relative z-10 flex items-center gap-2 text-[9px] text-slate-600">
          <ShieldCheck size={12} /> Your setup data is protected and can be edited later.
        </div>
      </aside>

      {role === 'startup' && (
        <section className="fb-signup-mobile-intro relative overflow-hidden px-5 py-5 text-white sm:px-8 lg:hidden">
          <div className="pointer-events-none absolute -right-12 -top-16 h-40 w-40 rounded-full bg-violet-500/10 blur-2xl" />
          <div className="pointer-events-none absolute -left-16 bottom-[-70px] h-36 w-36 rounded-full bg-emerald-400/[0.06] blur-2xl" />
          <div className="relative z-10 mx-auto flex w-full max-w-[920px] flex-col items-center gap-4 text-center sm:gap-5">
            <div className="min-w-0">
              <Link to="/" className="inline-flex items-center gap-2.5" aria-label="FounderBoard home">
                <img src="/founderboard-logo.png" alt="FounderBoard" className="h-7 w-7 rounded-md object-contain" />
                <span className="font-heading text-[13px] font-bold">FounderBoard</span>
              </Link>
              <h2 className="mx-auto mt-3 max-w-[520px] font-heading text-[20px] font-bold leading-[1.12] tracking-[-0.025em] sm:text-[22px]">
                Build your founder profile once. Use it everywhere.
              </h2>
              <p className="mx-auto mt-1.5 max-w-[560px] text-[10px] leading-[1.55] text-slate-400 sm:text-[11px]">
                Set up your startup profile to apply to programs, track progress, and keep your FounderBoard workspace ready.
              </p>
            </div>
            <div className="hidden shrink-0 rounded-full border border-emerald-300/15 bg-emerald-400/[0.07] px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-300 sm:block">
              Founder setup
            </div>
          </div>
        </section>
      )}

      <main className="fb-signup-form-enter min-h-0 overflow-y-visible bg-[#f6f7f9] lg:h-screen lg:overflow-y-auto">
        <div className="mx-auto w-full max-w-[920px] px-5 pb-7 pt-7 sm:px-8 sm:pt-8 lg:px-10 lg:pt-14 xl:px-12 xl:pb-8 xl:pt-16">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <button
                type="button"
                onClick={() => {
                  // The onboarding routes are only reachable while the user is authenticated.
                  // Clear the in-progress session before returning to /login, otherwise App.js
                  // immediately redirects the user back to onboarding.
                  localStorage.removeItem('token');
                  localStorage.removeItem('user');
                  window.location.assign('/login');
                }}
                className="mb-4 inline-flex min-w-[158px] items-center justify-center gap-2 rounded-md bg-violet-50 px-[1.15rem] py-2.5 text-[11px] font-bold text-violet-600 transition-colors hover:bg-violet-100 hover:text-violet-700"
              >
                <ArrowLeft size={14} weight="bold" />
                Back to sign in
              </button>
              <h1 className="font-heading text-[24px] font-bold tracking-[-0.025em] text-slate-950">
                {programContinuation ? 'Quick profile setup' : 'Set up your profile'}
              </h1>
              <p className="mt-1 text-[11px] text-slate-400">
                {programContinuation
                  ? 'Just the essentials — then you’ll continue directly to your program application.'
                  : 'Add the essentials now. You can complete the rest of your profile later.'}
              </p>
            </div>
            <span className="shrink-0 rounded-md bg-violet-50 px-2.5 py-1.5 text-[9px] font-bold uppercase tracking-[0.08em] text-violet-600">⏱ About 1 min</span>
          </div>

          {!hideProgress && (
            <div className="mb-5">
              <div className="h-1 overflow-hidden rounded-full bg-slate-200">
                <div className="fb-progress-fill h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-[width] duration-700 ease-out" style={{ width: `${completion}%` }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[9px] font-bold uppercase tracking-[0.06em] text-slate-400">
                <span>Step {step} of {totalSteps}: {current[0]}</span>
                <span className="text-emerald-600">{completion}% completed</span>
              </div>
            </div>
          )}

          <div key={`${role}-step-${step}`} className="fb-step-card overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_18px_50px_rgba(15,23,42,0.055)]">
            {!(role === 'startup' && singleSection) && (
              <div className="fb-step-header flex items-center justify-between border-b border-slate-100 bg-gradient-to-r from-white to-slate-50/70 px-5 py-4 sm:px-6">
                <div className="flex items-center gap-3">
                  {!singleSection && (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-[10px] font-bold text-white">{step}</div>
                  )}
                  <div>
                    <div className="text-[12px] font-bold text-slate-900">{current[0]}</div>
                    <div className="text-[9px] text-slate-400">{current[1]}</div>
                  </div>
                </div>
                {!singleSection && (
                  <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[8px] font-bold uppercase tracking-[0.08em] text-emerald-600">● Active step</span>
                )}
              </div>
            )}
            <div className="signup-form-content fb-step-form px-5 py-5 sm:px-6 sm:py-5.5">
              {children}
            </div>
          </div>

          {!singleSection && steps.length > 1 && (
            <div key={`step-list-${role}-${step}`} className="fb-step-list mt-4 space-y-2">
              {steps.map(([title, description], index) => {
                const number = index + 1;
                if (number === step) return null;
                const done = number < step;
                return (
                  <div key={title} className={`flex items-center justify-between rounded-xl border px-4 py-3 ${done ? 'border-emerald-100 bg-emerald-50/35' : 'border-slate-200/70 bg-white/45 opacity-70'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`flex h-6 w-6 items-center justify-center rounded-full border text-[9px] font-bold ${done ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300 text-slate-400'}`}>
                        {done ? <CheckCircle size={13} weight="fill" /> : number}
                      </div>
                      <div>
                        <div className="text-[11px] font-semibold text-slate-700">{title}</div>
                        <div className="text-[9px] text-slate-400">{description}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-[0.06em] text-slate-400">
                      {done ? 'Completed' : <><LockSimple size={10} /> Unlocks after Step {number - 1}</>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between text-[9px] text-slate-400">
            <span>* Required fields</span>
            <span>FounderBoard onboarding</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignupOnboardingShell;
